import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { getRulesFromState, get, pageContext } from 'ctm-util-functions';
import { SimpleFlexbox } from '../components/flexbox/flexbox';
import { SimpleValidationMessage } from '../components/form/validation_message/simple_validation_message';

/**
 * Renders children if the rule true or the rule is not supplied and passes the validation
 * @param {Object} WrappedComponent - Wrapped React Component.
 * @param {Object} withoutStore - To Return the component without connection to the store
 * @returns {Node|null} returns the children or null depending on the visibility rule
 */
const withRules = (WrappedComponent, withoutStore) => {
    class _WithRules extends PureComponent {

        constructor(props) {
            super(props);
            this.storeValidationInState(props);
        }

        componentDidUpdate = (prevProps) => {
            this.updateValidation(prevProps);
        };

        componentWillUnmount = () => {
            this.setInvalid();
            this.resetTouch();
        };

        resetTouch = () => {
            const { dispatch, identifier, stateKey } = this.props;
            if (identifier) {
                dispatch({
                    type: 'RESET_TOUCHES',
                    stateKey,
                    baseIdentifier: identifier
                });
            }
        };

        setInvalid = () => {
            this.storeValidationInState({
                ...this.props,
                valid: false
            });
        };

        updateValidation = (prevProps) => {
            const { valid } = this.props;
            if (prevProps.valid !== valid) {
                this.storeValidationInState(this.props);
            }
        };

        storeValidationInState = (props) => {
            const { storeValidation, identifier, valid, dispatch, stateKey } = props;
            if (storeValidation) {
                dispatch({
                    stateKey,
                    type: 'SET_OUTSIDE_FORM_VALIDATION',
                    value: valid,
                    identifier
                });
            }
        };

        render = () => {
            const { visible, children, valid, validationMessage, ...restProps } = this.props;
            if (!visible) return null;
            const wrappedComponent = (<WrappedComponent valid={valid} {...restProps}>{children}</WrappedComponent>);
            if (!validationMessage) return wrappedComponent;
            return (
                <SimpleFlexbox display="flex" column>
                    {wrappedComponent}
                    <SimpleValidationMessage validationMessage={validationMessage} />
                </SimpleFlexbox>
            );
        };

    }

    _WithRules.propTypes = {
        children: PropTypes.node,
        visible: PropTypes.bool,
        valid: PropTypes.bool,
        validationMessage: PropTypes.string,
        storeValidation: PropTypes.bool,
        dispatch: PropTypes.func,
        identifier: PropTypes.string,
        stateKey: PropTypes.string
    };

    _WithRules.defaultProps = {
        visible: true,
        stateKey: 'vertical.quote'
    };

    if (withoutStore) return _WithRules;
    return connect(mapStateToProps)(_WithRules);
};

/**
 * Gets the rules from state and maps it to the props
 * @param {Object} state - The application state
 * @param {Object} props - The componenets props
 * @returns {Object} returns the component props with the new rules
 */
const mapStateToProps = (state, props) => {
    const { identifier, displayErrorMessage, rules } = props;
    if (!rules) {
        return {};
    }
    const _rules = getRulesFromState(state, props);
    const visible = get(_rules, 'visibility.result', true);
    const disabled = get(_rules, 'disabled.result', false);
    const valid = get(_rules, 'validation.result', true);
    const touched = state.getIn(['vertical', 'quote', 'validation', 'touches', identifier]);
    let validationMessage = '';
    if (!valid && touched && displayErrorMessage) {
        // propably should handle this in a better way
        validationMessage = pageContext.getProps().models.validationModel.getErrorMessage(_rules.validation);
    }
    return {
        touched,
        visible,
        disabled,
        valid,
        validationMessage
    };
};

export { withRules };
